import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, formatDateBR } from '../App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Clock, User, FileText, AlertCircle, CheckCircle, Plus, Image as ImageIcon, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { colors } from '../theme';

// Helper para obter URL do comprovante (suporta Base64 e URLs relativas)
const getComprovanteUrl = (url) => {
  if (!url) return null;
  // Se já é uma data URL (Base64), retorna diretamente
  if (url.startsWith('data:')) return url;
  // Se é URL relativa, adiciona o API prefix
  return `${API}${url}`;
};

// Helper para verificar se é imagem (suporta Base64 e extensões)
const isImageUrl = (url) => {
  if (!url) return false;
  if (url.startsWith('data:image/')) return true;
  return /\.(jpg|jpeg|png|jfif)$/i.test(url);
};

// Helper para verificar se é PDF
const isPdfUrl = (url) => {
  if (!url) return false;
  if (url.startsWith('data:application/pdf')) return true;
  return /\.pdf$/i.test(url);
};

const ModalHistoricoPedido = ({ isOpen, onClose, pedido }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novaObservacao, setNovaObservacao] = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);
  const [mostrarFormObs, setMostrarFormObs] = useState(false);
  const [pedidoAtualizado, setPedidoAtualizado] = useState(pedido);
  const intervalRef = useRef(null);
  
  // Estados para controlar expansão dos accordions
  const [cteExpandido, setCteExpandido] = useState(false);
  const [comprovanteCorreiosExpandido, setComprovanteCorreiosExpandido] = useState(false);
  const [comprovanteEntregaExpandido, setComprovanteEntregaExpandido] = useState(false);

  // Sincronizar pedido quando props mudam
  useEffect(() => {
    setPedidoAtualizado(pedido);
  }, [pedido]);

  // Polling de 5 segundos quando modal está aberto
  useEffect(() => {
    if (isOpen && pedido?.id) {
      console.log('🔄 Modal Histórico - Iniciando atualização automática a cada 5 segundos');
      
      // Carregar dados iniciais
      carregarTudo();
      
      // Atualizar a cada 5 segundos
      intervalRef.current = setInterval(() => {
        console.log('🔄 Modal Histórico - Atualizando automaticamente');
        carregarTudo(false); // false = não mostra loading
      }, 30000); // 30 segundos
      
      // Limpar interval quando modal fechar ou pedido mudar
      return () => {
        if (intervalRef.current) {
          console.log('🛑 Modal Histórico - Parando atualização automática');
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isOpen, pedido?.id]);

  const carregarTudo = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('primaza_token');
      
      // Buscar histórico E dados atualizados do pedido em paralelo
      const [historicoResponse, pedidoResponse] = await Promise.all([
        axios.get(`${API}/pedidos/${pedido.id}/historico`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/pedidos`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      console.log('Histórico recebido do backend:', historicoResponse.data);
      setHistorico(historicoResponse.data);
      
      // Encontrar o pedido atualizado na lista
      const pedidoAtt = pedidoResponse.data.find(p => p.id === pedido.id);
      if (pedidoAtt) {
        console.log('Dados do pedido atualizados:', pedidoAtt);
        setPedidoAtualizado(pedidoAtt);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      console.error('Detalhes do erro:', error.response?.data);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const carregarHistorico = async (showLoading = true) => {
    // Manter função legacy para compatibilidade
    carregarTudo(showLoading);
  };

  const adicionarObservacao = async () => {
    if (!novaObservacao.trim()) return;

    setSalvandoObs(true);
    try {
      const token = localStorage.getItem('primaza_token');
      const response = await axios.post(
        `${API}/pedidos/${pedido.id}/historico/observacao`,
        { observacao: novaObservacao },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Observação adicionada:', response.data);
      setNovaObservacao('');
      setMostrarFormObs(false);
      carregarHistorico();
    } catch (error) {
      console.error('Erro ao adicionar observação:', error);
      console.error('Detalhes do erro:', error.response?.data);
      alert('Erro ao adicionar observação');
    } finally {
      setSalvandoObs(false);
    }
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    // Converter para horário de Brasília (UTC-3)
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });
  };

  const formatarTextoComDatas = (texto) => {
    if (!texto) return texto;
    // Regex para encontrar datas no formato YYYY-MM-DD
    const regexData = /(\d{4})-(\d{2})-(\d{2})/g;
    return texto.replace(regexData, (match, ano, mes, dia) => {
      return `${dia}/${mes}/${ano}`;
    });
  };

  const getIconePorTipo = (tipo) => {
    if (tipo === 'Observação') return <FileText size={16} />;
    if (tipo === 'Criação') return <CheckCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getCorPorRole = (role) => {
    const cores = {
      admin: '#DC2626',
      vendedor: '#2563EB',
      lancador: '#059669'
    };
    return cores[role?.toLowerCase()] || '#6B7280';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" style={{ color: colors.primaza.secondary }}>
            <Clock size={24} />
            Histórico do Pedido
          </DialogTitle>
          <DialogDescription>
            <div>
              Pedido: <strong>{pedidoAtualizado?.pedido || 'N/A'}</strong> | Cliente: <strong>{pedidoAtualizado?.nome_cliente || 'N/A'}</strong>
            </div>
            {pedidoAtualizado?.created_at && (
              <div className="mt-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded border border-blue-200">
                📅 <strong>Entrada no Sistema:</strong>{' '}
                {new Date(pedidoAtualizado.created_at).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'America/Sao_Paulo'
                })}
                {' '}(Data de Faturamento)
              </div>
            )}
            {pedidoAtualizado?.prev_entrega && (
              <div className="mt-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded border border-green-200">
                🚚 <strong>Previsão de Entrega:</strong>{' '}
                {formatDateBR(pedidoAtualizado.prev_entrega)}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto pr-2">
          {/* Dados do CTE - Accordion */}
          {pedidoAtualizado?.numero_cte && (
            <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
              {/* Header do Accordion - Sempre visível */}
              <button
                onClick={() => setCteExpandido(!cteExpandido)}
                className="w-full p-3 bg-blue-50 hover:bg-blue-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="text-blue-600" size={20} />
                  <div className="text-left">
                    <h4 className="font-semibold text-blue-800 text-sm">Conhecimento de Transporte (CTE)</h4>
                    <p className="text-xs text-blue-600">CTE {pedidoAtualizado.numero_cte} • {pedidoAtualizado.tipo_frete || 'CIF'} • R$ {pedidoAtualizado.valor_frete_cte}</p>
                  </div>
                </div>
                {cteExpandido ? <ChevronUp className="text-blue-600" size={20} /> : <ChevronDown className="text-blue-600" size={20} />}
              </button>
              
              {/* Conteúdo Expandível */}
              {cteExpandido && (
                <div className="p-4 bg-white border-t-2 border-blue-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-gray-600 text-xs mb-1">Número do CTE</p>
                      <p className="font-bold text-blue-900">{pedidoAtualizado.numero_cte}</p>
                    </div>
                    
                    {pedidoAtualizado.chave_cte && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Chave do CTE</p>
                        <p className="font-mono text-xs text-blue-900 break-all">{pedidoAtualizado.chave_cte}</p>
                      </div>
                    )}
                    
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-gray-600 text-xs mb-1">Valor do Frete</p>
                      <p className="font-bold text-blue-900">
                        {typeof pedidoAtualizado.valor_frete_cte === 'string' && pedidoAtualizado.valor_frete_cte.includes('R$') 
                          ? pedidoAtualizado.valor_frete_cte 
                          : `R$ ${pedidoAtualizado.valor_frete_cte}`}
                      </p>
                    </div>
                    
                    {pedidoAtualizado.data_emissao_cte && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-gray-600 text-xs mb-1">Data de Emissão</p>
                        <p className="font-medium text-blue-900">
                          {formatDateBR(pedidoAtualizado.data_emissao_cte)}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                    <p className="text-xs text-gray-600">
                      <strong>Tipo de Frete:</strong> {pedidoAtualizado.tipo_frete === 'CIF' ? 'CIF (Frete embutido na NF)' : 'FOB (Frete separado)'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comprovante dos Correios - Accordion */}
          {pedidoAtualizado?.comprovante_correios_url && (
            <div className="border-2 border-yellow-200 rounded-lg overflow-hidden">
              {/* Header do Accordion */}
              <button
                onClick={() => setComprovanteCorreiosExpandido(!comprovanteCorreiosExpandido)}
                className="w-full p-3 bg-yellow-50 hover:bg-yellow-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FileText className="text-yellow-600" size={20} />
                  <div className="text-left">
                    <h4 className="font-semibold text-yellow-800 text-sm">Comprovante de Postagem - Correios</h4>
                    <p className="text-xs text-yellow-600">Documento anexado • Clique para ver detalhes</p>
                  </div>
                  <CheckCircle size={16} className="text-yellow-600" />
                </div>
                {comprovanteCorreiosExpandido ? <ChevronUp className="text-yellow-600" size={20} /> : <ChevronDown className="text-yellow-600" size={20} />}
              </button>
              
              {/* Conteúdo Expandível */}
              {comprovanteCorreiosExpandido && (
                <div className="p-4 bg-white border-t-2 border-yellow-200">
                  {/* Preview da imagem se for JPG/PNG */}
                  {isImageUrl(pedidoAtualizado.comprovante_correios_url) && (
                    <div className="mb-3">
                      <img 
                        src={getComprovanteUrl(pedidoAtualizado.comprovante_correios_url)}
                        alt="Comprovante Correios"
                        className="max-w-full h-auto rounded-lg border border-yellow-300 cursor-pointer hover:opacity-90 transition"
                        style={{ maxHeight: '200px' }}
                        onClick={() => window.open(getComprovanteUrl(pedidoAtualizado.comprovante_correios_url), '_blank')}
                      />
                      <p className="text-xs text-gray-500 mt-1">Clique na imagem para ampliar</p>
                    </div>
                  )}
                  
                  {/* Ícone de PDF se for PDF */}
                  {isPdfUrl(pedidoAtualizado.comprovante_correios_url) && (
                    <div className="mb-3 p-3 bg-yellow-50 rounded border border-yellow-300">
                      <div className="flex items-center gap-2">
                        <FileText className="text-red-600" size={20} />
                        <span className="text-sm font-medium">Documento PDF</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(getComprovanteUrl(pedidoAtualizado.comprovante_correios_url), '_blank')}
                    className="text-yellow-700 hover:bg-yellow-100 border-yellow-400"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Abrir em Nova Aba
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Comprovante de Entrega - Accordion */}
          {pedidoAtualizado?.comprovante_entrega_url && (
            <div className="border-2 border-green-200 rounded-lg overflow-hidden">
              {/* Header do Accordion */}
              <button
                onClick={() => setComprovanteEntregaExpandido(!comprovanteEntregaExpandido)}
                className="w-full p-3 bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <ImageIcon className="text-green-600" size={20} />
                  <div className="text-left">
                    <h4 className="font-semibold text-green-800 text-sm">Comprovante de Entrega</h4>
                    <p className="text-xs text-green-600">
                      {pedidoAtualizado.data_entrega_real 
                        ? `Entregue em ${formatDateBR(pedidoAtualizado.data_entrega_real)}` 
                        : 'Documento anexado'}
                    </p>
                  </div>
                  <CheckCircle size={16} className="text-green-600" />
                </div>
                {comprovanteEntregaExpandido ? <ChevronUp className="text-green-600" size={20} /> : <ChevronDown className="text-green-600" size={20} />}
              </button>
              
              {/* Conteúdo Expandível */}
              {comprovanteEntregaExpandido && (
                <div className="p-4 bg-white border-t-2 border-green-200">
                  {/* Preview da imagem se for JPG/PNG */}
                  {isImageUrl(pedidoAtualizado.comprovante_entrega_url) && (
                    <div className="mb-3">
                      <img 
                        src={getComprovanteUrl(pedidoAtualizado.comprovante_entrega_url)}
                        alt="Comprovante de Entrega"
                        className="max-w-full h-auto rounded-lg border border-green-300 cursor-pointer hover:opacity-90 transition"
                        style={{ maxHeight: '200px' }}
                        onClick={() => window.open(getComprovanteUrl(pedidoAtualizado.comprovante_entrega_url), '_blank')}
                      />
                      <p className="text-xs text-gray-500 mt-1">Clique na imagem para ampliar</p>
                    </div>
                  )}
                  
                  {/* Ícone de PDF se for PDF */}
                  {isPdfUrl(pedidoAtualizado.comprovante_entrega_url) && (
                    <div className="mb-3 p-3 bg-green-50 rounded border border-green-300">
                      <div className="flex items-center gap-2">
                        <FileText className="text-red-600" size={20} />
                        <span className="text-sm font-medium">Documento PDF</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(getComprovanteUrl(pedidoAtualizado.comprovante_entrega_url), '_blank')}
                    className="text-green-700 hover:bg-green-100 border-green-400"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Abrir em Nova Aba
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Botão Adicionar Observação */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMostrarFormObs(!mostrarFormObs)}
              style={{
                borderColor: colors.primaza.primary,
                color: colors.primaza.secondary
              }}
            >
              <Plus size={16} className="mr-2" />
              {mostrarFormObs ? 'Cancelar' : 'Adicionar Observação'}
            </Button>
          </div>

          {/* Formulário Nova Observação */}
          {mostrarFormObs && (
            <div className="p-4 border rounded-lg bg-gray-50 space-y-3">
              <Label htmlFor="nova-obs">Nova Observação</Label>
              <Textarea
                id="nova-obs"
                value={novaObservacao}
                onChange={(e) => setNovaObservacao(e.target.value)}
                placeholder="Digite sua observação..."
                rows={3}
                className="resize-none"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNovaObservacao('');
                    setMostrarFormObs(false);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={adicionarObservacao}
                  disabled={salvandoObs || !novaObservacao.trim()}
                  style={{
                    backgroundColor: colors.primaza.primary,
                    color: 'white'
                  }}
                >
                  {salvandoObs ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          )}

          {/* Lista de Histórico */}
          <div className="rounded-md border p-4">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                Carregando histórico...
              </div>
            ) : historico.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                Nenhum registro de histórico encontrado
              </div>
            ) : (
              <div className="space-y-4">
                {historico.map((item, index) => (
                  <div key={item.id || index}>
                    <div className="flex gap-3">
                      {/* Ícone */}
                      <div
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: getCorPorRole(item.alterado_por_role) + '20',
                          color: getCorPorRole(item.alterado_por_role)
                        }}
                      >
                        {getIconePorTipo(item.campo_alterado)}
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1 space-y-1">
                        {/* Cabeçalho */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">
                              {item.campo_alterado}
                            </span>
                            <Badge
                              variant="outline"
                              style={{
                                borderColor: getCorPorRole(item.alterado_por_role),
                                color: getCorPorRole(item.alterado_por_role)
                              }}
                            >
                              {item.alterado_por_role}
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatarData(item.created_at)}
                          </span>
                        </div>

                        {/* Usuário */}
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <User size={12} />
                          {item.alterado_por}
                          {/* Indicador de alteração automática ou manual */}
                          {item.tipo_alteracao && (
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              item.tipo_alteracao === 'automatico' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {item.tipo_alteracao === 'automatico' ? '🤖 Automático' : '👤 Manual'}
                            </span>
                          )}
                        </div>
                        
                        {/* IP do usuário (se disponível) */}
                        {item.ip_usuario && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            IP: {item.ip_usuario}
                          </div>
                        )}

                        {/* Alteração (se houver) */}
                        {item.valor_anterior && item.valor_novo && (
                          <div className="text-sm mt-2 p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-red-600 line-through">{item.valor_anterior}</span>
                              <span className="text-gray-400">→</span>
                              <span className="text-green-600 font-medium">{item.valor_novo}</span>
                            </div>
                          </div>
                        )}

                        {/* Observação */}
                        {item.observacao && (
                          <div className="text-sm mt-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                            <p className="text-gray-700">{formatarTextoComDatas(item.observacao)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {index < historico.length - 1 && <Separator className="my-4" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rodapé */}
          <div className="flex justify-between items-center text-xs text-gray-500 pt-2">
            <span>{historico.length} registro(s) de histórico</span>
            <Button variant="ghost" size="sm" onClick={carregarHistorico}>
              Atualizar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalHistoricoPedido;
